import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Group,
  Modal,
  ScrollAreaAutosize,
  Text,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { IconCopy, IconCopyCheck } from "@tabler/icons-react";
import i18n from "src/i18n";

import { showInfoNotification } from "@utils/showNotification";

import { useGenerateMeetingLinkForPatient } from "@hooks/queries/useGenerateMeetingLinkForPatient";


interface Props {
  setIsCalling: (arg: boolean) => void;
  opened: boolean;
  close: () => void;
  consultationId: string;
}

export const JoinCallWarningModal = ({
  setIsCalling,
  opened,
  close,
  consultationId,
}: Props) => {
  const { t } = useTranslation();
  const clipboard = useClipboard({ timeout: 500 });

  const { data } = useGenerateMeetingLinkForPatient({ consultationId });
  const teleconsultationLink = data?.data ?? "";

  useEffect(() => {
    if (clipboard.copied) {
      showInfoNotification({ message: "Link copied" });
    }
  }, [clipboard.copied]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("start-call")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}
      styles={{
        body: {
          minHeight: "auto",
        },
      }}>
      <>
        <Text lh={1.5} fz={14}>
          {i18n.t("start-call-warning")}
        </Text>
        <Alert
          mt={"md"}
          variant="light"
          color="blue"
          icon={
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={() => clipboard.copy(teleconsultationLink)}>
              {clipboard.copied ? (
                <IconCopyCheck size={18} />
              ) : (
                <IconCopy size={18} />
              )}
            </ActionIcon>
          }>
          <Text fz={14} lh={1.4} c="blue">
            {t("copy-link-for-patients")}
          </Text>
          <Group mt={"xs"}>
            <Anchor
              lh={1.4}
              inherit
              fw={400}
              c="accent.5"
              onClick={() => clipboard.copy(teleconsultationLink)}>
              {teleconsultationLink}
            </Anchor>
          </Group>
        </Alert>

        <Group justify="end" mt={"xl"}>
          <Button
            variant="outline"
            onClick={() => {
              close();
            }}>
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              setIsCalling(true);
              close();
            }}>
            {t("start-call")}
          </Button>
        </Group>
      </>
    </Modal>
  );
};
